import React, { useEffect, useMemo, useRef, useState } from 'react';
import useAgentMqtt from '../../hooks/useAgentMqtt';
import './AgentNode.css';

/**
 * Available icons for agent nodes.
 * Each icon has a key, label, and SVG path data.
 */
export const AGENT_ICONS = {
  bot: {
    label: 'Bot',
    viewBox: '0 0 16 16',
    path: 'M8 5.333V2.667H5.333M1.333 9.333h1.334M13.333 9.333h1.334M10 8.667V10M6 8.667V10M4 5.333h8a1.333 1.333 0 011.333 1.334V12A1.333 1.333 0 0112 13.333H4A1.333 1.333 0 012.667 12V6.667A1.333 1.333 0 014 5.333z',
    stroke: true,
  },
  public: {
    label: 'Globe',
    viewBox: '0 0 24 24',
    path: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z',
  },
  smartToy: {
    label: 'Robot',
    viewBox: '0 0 24 24',
    path: 'M20 9V7c0-1.1-.9-2-2-2h-3c0-1.66-1.34-3-3-3S9 3.34 9 5H6c-1.1 0-2 .9-2 2v2c-1.66 0-3 1.34-3 3s1.34 3 3 3v4c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2v-4c1.66 0 3-1.34 3-3s-1.34-3-3-3zM7.5 11.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5S9.83 13 9 13s-1.5-.67-1.5-1.5zM16 17H8v-2h8v2zm-1-4c-.83 0-1.5-.67-1.5-1.5S14.17 10 15 10s1.5.67 1.5 1.5S15.83 13 15 13z',
  },
  psychology: {
    label: 'Brain',
    viewBox: '0 0 24 24',
    path: 'M13 8.57c-.79 0-1.43.64-1.43 1.43s.64 1.43 1.43 1.43 1.43-.64 1.43-1.43-.64-1.43-1.43-1.43z M13 3C9.25 3 6.2 5.94 6.02 9.64L4.1 12.2c-.25.33-.01.8.4.8H6v3c0 1.1.9 2 2 2h1v3h7v-4.68c2.36-1.12 4-3.53 4-6.32 0-3.87-3.13-7-7-7zm3 7c0 .13-.01.26-.02.39l.83.66c.08.06.1.16.05.25l-.8 1.39c-.05.09-.16.11-.24.09l-.99-.4c-.21.16-.43.29-.67.39L14 13.83c-.01.1-.1.17-.2.17h-1.6c-.1 0-.18-.07-.2-.17l-.15-1.06c-.25-.1-.47-.23-.68-.39l-.99.4c-.09.03-.2 0-.25-.09l-.8-1.39c-.05-.08-.03-.19.05-.25l.84-.66c-.01-.13-.02-.26-.02-.39s.01-.26.02-.39l-.83-.66c-.08-.06-.1-.16-.05-.25l.8-1.39c.05-.09.15-.11.24-.09l.99.4c.21-.16.43-.29.67-.39L11 6.17c.02-.1.1-.17.2-.17h1.6c.1 0 .18.07.2.17l.15 1.06c.24.1.46.23.67.39l.99-.4c.09-.04.19 0 .24.09l.8 1.39c.05.09.03.19-.05.25l-.83.66c.01.13.03.26.03.39z',
  },
  visibility: {
    label: 'Eye',
    viewBox: '0 0 24 24',
    path: 'M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z',
  },
  build: {
    label: 'Wrench',
    viewBox: '0 0 24 24',
    path: 'M22.7 19l-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4z',
  },
  barChart: {
    label: 'Chart',
    viewBox: '0 0 24 24',
    path: 'M5 9.2h3V19H5zM10.6 5h2.8v14h-2.8zm5.6 8H19v6h-2.8z',
  },
  security: {
    label: 'Shield',
    viewBox: '0 0 24 24',
    path: 'M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z',
  },
  inventory: {
    label: 'Box',
    viewBox: '0 0 24 24',
    path: 'M20 2H4c-1 0-2 .9-2 2v3.01c0 .72.43 1.34 1 1.69V20c0 1.1 1.1 2 2 2h14c.9 0 2-.9 2-2V8.7c.57-.35 1-.97 1-1.69V4c0-1.1-1-2-2-2zm-5 12H9v-2h6v2zm5-7H4V4h16v3z',
  },
  speed: {
    label: 'Gauge',
    viewBox: '0 0 24 24',
    path: 'M20.38 8.57l-1.23 1.85a8 8 0 0 1-.22 7.58H5.07A8 8 0 0 1 15.58 6.85l1.85-1.23A10 10 0 0 0 3.35 19a2 2 0 0 0 1.72 1h13.85a2 2 0 0 0 1.74-1 10 10 0 0 0-.27-10.44zm-9.79 6.84a2 2 0 0 0 2.83 0l5.66-8.49-8.49 5.66a2 2 0 0 0 0 2.83z',
  },
  lightbulb: {
    label: 'Lightbulb',
    viewBox: '0 0 24 24',
    path: 'M9 21c0 .5.4 1 1 1h4c.6 0 1-.5 1-1v-1H9v1zm3-19C8.1 2 5 5.1 5 9c0 2.4 1.2 4.5 3 5.7V17c0 .5.4 1 1 1h6c.6 0 1-.5 1-1v-2.3c1.8-1.3 3-3.4 3-5.7 0-3.9-3.1-7-7-7z',
  },
};

/**
 * AgentNode component — collapsed pill by default, click to expand thoughts.
 * Visual style anchored to the appbeacon pill aesthetic.
 *
 * @param {Object} props
 * @param {string} props.name - Display name of the agent
 * @param {string} props.topicBase - Base MQTT topic path
 * @param {Object} props.position - { x: number, y: number } as percentages
 * @param {Object} props.mqttConfig - MQTT connection configuration
 * @param {Object} props.style - Optional style overrides { color: string }
 * @param {Object} props.simulatedState - Optional simulated state { thoughts, status, isHighlighted, isConnected }
 * @param {string} props.icon - Icon key from AGENT_ICONS (defaults to 'public')
 * @param {boolean} props.devMode - Enable drag handle cursor
 */
const AgentNode = ({
  name,
  topicBase,
  position = { x: 50, y: 50 },
  mqttConfig,
  style = {},
  simulatedState = null,
  icon = 'bot',
  onPointerDown,
  devMode = false,
}) => {
  const { color = '#B07AA1' } = style;

  const mqttState = useAgentMqtt(
    simulatedState ? null : mqttConfig,
    simulatedState ? null : topicBase
  );

  const effectiveState = simulatedState || mqttState;
  const thoughts = useMemo(() => effectiveState?.thoughts || [], [effectiveState?.thoughts]);
  const status = effectiveState?.status || 'idle';
  const isHighlighted = effectiveState?.isHighlighted || false;

  const [hasNewThought, setHasNewThought] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const prevThoughtsLengthRef = useRef(0);
  const expandedRef = useRef(null);

  // Flash effect + auto-scroll when new thought arrives
  useEffect(() => {
    const currentLength = thoughts?.length || 0;
    if (currentLength > prevThoughtsLengthRef.current) {
      setHasNewThought(true);
      const timer = setTimeout(() => setHasNewThought(false), 300);
      if (expandedRef.current) {
        expandedRef.current.scrollTop = expandedRef.current.scrollHeight;
      }
      prevThoughtsLengthRef.current = currentLength;
      return () => clearTimeout(timer);
    }
    prevThoughtsLengthRef.current = currentLength;
  }, [thoughts]);

  const pillClasses = [
    'agent-node-pill',
    status === 'thinking' ? 'thinking' : '',
    isHighlighted ? 'highlighted' : '',
    hasNewThought ? 'new-thought' : '',
  ].filter(Boolean).join(' ');

  return (
    <div
      className="agent-node-wrapper"
      style={{
        left: `${position.x}%`,
        top: `${position.y}%`,
        '--agent-color': color,
        ...(devMode ? { cursor: 'grab' } : {}),
      }}
      onPointerDown={onPointerDown}
    >
      {/* Collapsed pill */}
      <div
        className={pillClasses}
        onClick={() => setExpanded((prev) => !prev)}
      >
        <svg
          className="agent-node-pill-icon"
          viewBox={AGENT_ICONS[icon]?.viewBox || AGENT_ICONS.bot.viewBox}
          fill={AGENT_ICONS[icon]?.stroke ? 'none' : 'currentColor'}
          stroke={AGENT_ICONS[icon]?.stroke ? 'currentColor' : undefined}
          strokeLinecap={AGENT_ICONS[icon]?.stroke ? 'round' : undefined}
          strokeLinejoin={AGENT_ICONS[icon]?.stroke ? 'round' : undefined}
        >
          <path d={AGENT_ICONS[icon]?.path || AGENT_ICONS.bot.path} />
        </svg>
        <span className="agent-node-pill-name"><span className="agent-node-pill-prefix">Agent:</span> {name}</span>
        <span className={`agent-node-pill-dot ${status}`} />
      </div>

      {/* Expanded thoughts panel */}
      {expanded && (
        <div className="agent-node-expanded" ref={expandedRef}>
          {thoughts.length > 0 ? (
            <ul className="agent-node-thoughts">
              {thoughts.map((thought, index) => (
                <li key={index} className="agent-node-thought">
                  {thought}
                </li>
              ))}
            </ul>
          ) : (
            <div className="agent-node-empty">
              {status === 'idle' ? 'Waiting for input...' : ''}
            </div>
          )}
        </div>
      )}

      {/* Connector stem + dot */}
      <div className="agent-node-stem" />
      <div className="agent-node-ring" />
    </div>
  );
};

export default AgentNode;
